"use client";

import { useMemo, useState } from "react";
import { formOptions } from "@tanstack/react-form";
import { useAppForm } from "../shared/hooks/form";
import { useTranslations } from "next-intl";
import { EVENT_RESOURCE_FORM_DEFAULTS, eventResourceFieldConfig } from "./constants";
import { requiredString, optionalString, emailValidator, phoneValidator, countryValidator } from "../shared/validators";
import { createValidationMessages, setContentGateSubmitted, enrichWithUTMs } from "../shared/utils";
import type { EventResourceRegistrationPayload } from "./types";
import type { HubSpotSubmitResult } from "@/app/_api/hubspot/shared/types";
import { SubmitButton } from "../shared/SubmitButton";
import { useAnalytics } from "@ui/hooks/useAnalytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import { useContentGateSuccess } from "../../ContentGate";

const formOpts = formOptions({
  defaultValues: EVENT_RESOURCE_FORM_DEFAULTS,
});

export interface EventResourceFormProps {
  formId: string;
  resourceTitle: string;
  onSubmit?: (payload: EventResourceRegistrationPayload) => Promise<HubSpotSubmitResult>;
}

export function EventResourceForm({ formId, resourceTitle, onSubmit }: EventResourceFormProps) {
  const t = useTranslations("ClientKeys.webinar_form");
  const tValidation = useTranslations("ClientKeys.forms.validation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { captureEvent } = useAnalytics();
  const onSuccess = useContentGateSuccess();

  const messages = useMemo(() => createValidationMessages(tValidation), [tValidation]);
  const validators = useMemo(
    () => ({
      firstname: requiredString(messages, 100),
      lastname: requiredString(messages, 100),
      email: emailValidator(messages),
      phone: phoneValidator(messages),
      country: countryValidator(messages),
      company: requiredString(messages, 200),
      website: requiredString(messages, 500),
      jobtitle: requiredString(messages, 200),
      linkedin__c: optionalString(messages, 500),
    }),
    [messages],
  );

  const form = useAppForm({
    ...formOpts,
    onSubmit: async ({ value }) => {
      setSubmitError(null);

      const payload: EventResourceRegistrationPayload = {
        ...enrichWithUTMs(value),
        webinar_registration_date: new Date().toISOString(),
        latest_webinar_attended: resourceTitle,
      };

      try {
        if (onSubmit) {
          const result = await onSubmit(payload);
          if (result.success) {
            setContentGateSubmitted(formId);
            captureEvent(EventName.mw_webinar_form_submitted, {
              form_id: formId,
              webinar_title: resourceTitle,
            });
            onSuccess();
          } else {
            setSubmitError(result.error || t("errors.submission_failed"));
          }
        } else {
          setContentGateSubmitted(formId);
          captureEvent(EventName.mw_webinar_form_submitted, {
            form_id: formId,
            webinar_title: resourceTitle,
          });
          onSuccess();
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to submit event resource registration");
        setSubmitError(t("errors.submission_failed"));
        throw error;
      }
    },
  });

  const fc = eventResourceFieldConfig;

  return (
    <div className='tw-w-full'>
      <form
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.firstname.name}
              validators={{ onMount: validators.firstname, onChange: validators.firstname }}
            >
              {field => (
                <field.Input
                  name={fc.firstname.name}
                  label={t("fields.firstname.label")}
                  placeholder={t("fields.firstname.placeholder")}
                  required={fc.firstname.required}
                />
              )}
            </form.AppField>
          </div>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.lastname.name}
              validators={{ onMount: validators.lastname, onChange: validators.lastname }}
            >
              {field => (
                <field.Input
                  name={fc.lastname.name}
                  label={t("fields.lastname.label")}
                  placeholder={t("fields.lastname.placeholder")}
                  required={fc.lastname.required}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField name={fc.email.name} validators={{ onMount: validators.email, onChange: validators.email }}>
              {field => (
                <field.Input
                  name={fc.email.name}
                  label={t("fields.email.label")}
                  placeholder={t("fields.email.placeholder")}
                  type='email'
                  required={fc.email.required}
                />
              )}
            </form.AppField>
          </div>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.company.name}
              validators={{ onMount: validators.company, onChange: validators.company }}
            >
              {field => (
                <field.Input
                  name={fc.company.name}
                  label={t("fields.company.label")}
                  placeholder={t("fields.company.placeholder")}
                  required={fc.company.required}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.website.name}
              validators={{ onMount: validators.website, onChange: validators.website }}
            >
              {field => (
                <field.Input
                  name={fc.website.name}
                  label={t("fields.website.label")}
                  placeholder={t("fields.website.placeholder")}
                  required={fc.website.required}
                />
              )}
            </form.AppField>
          </div>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.jobtitle.name}
              validators={{ onMount: validators.jobtitle, onChange: validators.jobtitle }}
            >
              {field => (
                <field.Input
                  name={fc.jobtitle.name}
                  label={t("fields.jobtitle.label")}
                  placeholder={t("fields.jobtitle.placeholder")}
                  required={fc.jobtitle.required}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.linkedin__c.name}
              validators={{ onMount: validators.linkedin__c, onChange: validators.linkedin__c }}
            >
              {field => (
                <field.Input
                  name={fc.linkedin__c.name}
                  label={t("fields.linkedin__c.label")}
                  placeholder={t("fields.linkedin__c.placeholder")}
                  required={fc.linkedin__c.required}
                />
              )}
            </form.AppField>
          </div>
          <div className='tw-flex-1 tw-min-w-0'>
            <form.AppField
              name={fc.country.name}
              validators={{ onMount: validators.country, onChange: validators.country }}
            >
              {field => (
                <field.CountrySelect
                  name={fc.country.name}
                  label={t("fields.country.label")}
                  required={fc.country.required}
                  showTooltip={false}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <form.Subscribe selector={state => state.values.country}>
          {country => (
            <form.AppField
              name={fc.phone.name}
              validators={{
                onChangeListenTo: ["country"],
                onMount: validators.phone,
                onChange: validators.phone,
              }}
            >
              {field => (
                <field.PhoneInput
                  name={fc.phone.name}
                  label={t("fields.phone.label")}
                  placeholder={t("fields.phone.placeholder")}
                  required={fc.phone.required}
                  country={country}
                  compactOnMobile
                />
              )}
            </form.AppField>
          )}
        </form.Subscribe>

        <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
          {({ canSubmit, isSubmitting }) => (
            <SubmitButton
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              submitLabel={t("buttons.submit")}
              submittingLabel={t("buttons.submitting")}
              submitError={submitError}
            />
          )}
        </form.Subscribe>
      </form>
    </div>
  );
}
